import { PLAYLIST_SCHEMA_VERSION } from "./constants";
import { classifyError, storageError } from "./errors";
import { getLocalStorage } from "./PlatformCapabilities";
import { validateMeta, validateStoredPlaylist, validateTombstone } from "./records";

const DEFAULT_KEY = "xp.playlists.v1";

const upsert = (list, items) => {

    const next = list.slice();

    items.forEach((item) => {
        for (let i = 0; i < next.length; i++) {
            if (next[i].uid === item.uid) {
                next[i] = item;
                return;
            }
        }
        next.push(item);
    });

    return next;

};

/**
 * The playlist store: the whole state is one JSON value under one key
 * ({ v, playlists, tombstones, meta, quarantine }), so a write is atomic. Every write is checked by reading
 * it back, which catches a storage that accepts `setItem` and silently keeps nothing.
 *
 * A full localStorage surfaces as QUOTA_EXCEEDED and nothing is written.
 * `quarantine` holds records this build can't read; it is carried through every write so a rewrite never
 * destroys them.
 *
 * `change`: { put?, remove? (uids), putTombstones?, removeTombstones? (uids), meta? (replaces it), clear? }
 */
export class LocalStorageAdapter {

    constructor(options = {}) {
        this.kind = "localstorage";
        this.options = options;
        this.key = options.key || DEFAULT_KEY;
    }

    async probe() {

        const storage = this.storage();
        const probeKey = this.key + ".probe";
        const nonce = Math.random().toString(36).slice(2) + String(Date.now());

        try {
            storage.setItem(probeKey, nonce);
            const read = storage.getItem(probeKey);
            storage.removeItem(probeKey);
            if (read !== nonce) throw storageError("STORAGE_ERROR", "probe_mismatch");
        } catch (e) {
            throw classifyError(e, { code: "STORAGE_UNAVAILABLE", reason: "probe_failed" });
        }

    }

    async readAll() {

        const blob = this.load();

        return {
            playlists: blob.playlists,
            tombstones: blob.tombstones,
            meta: blob.meta,
            corrupt: blob.quarantine.length + (blob.unreadable ? 1 : 0),
        };

    }

    async commit(change) {

        const storage = this.storage();
        const blob = this.load();

        let playlists = change.clear ? [] : blob.playlists;
        let tombstones = change.clear ? [] : blob.tombstones;

        if (change.remove && change.remove.length) {
            const drop = {};
            change.remove.forEach((uid) => { drop[uid] = true; });
            playlists = playlists.filter((p) => !drop[p.uid]);
        }

        if (change.put && change.put.length) playlists = upsert(playlists, change.put);

        if (change.removeTombstones && change.removeTombstones.length) {
            const drop = {};
            change.removeTombstones.forEach((uid) => { drop[uid] = true; });
            tombstones = tombstones.filter((t) => !drop[t.uid]);
        }

        if (change.putTombstones && change.putTombstones.length) tombstones = upsert(tombstones, change.putTombstones);

        const next = {
            v: PLAYLIST_SCHEMA_VERSION,
            playlists,
            tombstones,
            meta: change.meta || blob.meta,
            quarantine: blob.quarantine,
        };

        let text;

        try {
            text = JSON.stringify(next);
        } catch (e) {
            throw storageError("STORAGE_ERROR", "serialization");
        }

        try {
            storage.setItem(this.key, text);
        } catch (e) {
            throw classifyError(e, { code: "STORAGE_ERROR", reason: "set_item" });
        }

        let back = null;

        try {
            back = storage.getItem(this.key);
        } catch (e) { }

        if (back !== text) throw storageError("STORAGE_ERROR", "verify_failed");

    }

    storage() {

        const storage = this.options.storage !== undefined ? this.options.storage : getLocalStorage();

        if (!storage) throw storageError("STORAGE_UNAVAILABLE", "no_localstorage");

        return storage;

    }

    load() {

        const storage = this.storage();
        let raw;

        try {
            raw = storage.getItem(this.key);
        } catch (e) {
            throw classifyError(e, { code: "STORAGE_UNAVAILABLE", reason: "get_item" });
        }

        const empty = (unreadable) => ({ v: PLAYLIST_SCHEMA_VERSION, playlists: [], tombstones: [], meta: null, quarantine: [], unreadable });

        if (raw === null || raw === "") return empty(false);

        let parsed;

        try {
            parsed = JSON.parse(raw);
        } catch (e) {
            this.keepCorruptCopy(storage, raw);
            return empty(true);
        }

        // a newer schema may not have a `playlists` array at all; it is theirs, not corrupt, and must not be touched
        if (parsed && typeof parsed === "object" && typeof parsed.v === "number" && parsed.v > PLAYLIST_SCHEMA_VERSION) {
            throw storageError("UNSUPPORTED_SCHEMA", "newer_schema");
        }

        if (!parsed || typeof parsed !== "object" || !Array.isArray(parsed.playlists)) {
            this.keepCorruptCopy(storage, raw);
            return empty(true);
        }

        const playlists = [];
        const quarantine = Array.isArray(parsed.quarantine) ? parsed.quarantine.slice() : [];

        parsed.playlists.forEach((item) => {
            const record = validateStoredPlaylist(item);
            if (record) playlists.push(record);
            else quarantine.push(item);
        });

        const tombstones = [];

        (Array.isArray(parsed.tombstones) ? parsed.tombstones : []).forEach((item) => {
            const tomb = validateTombstone(item);
            if (tomb) tombstones.push(tomb);
            else quarantine.push(item);
        });

        return { v: PLAYLIST_SCHEMA_VERSION, playlists, tombstones, meta: validateMeta(parsed.meta), quarantine };

    }

    // a blob that isn't even JSON is set aside once, so a rewrite doesn't destroy the only copy
    keepCorruptCopy(storage, raw) {

        const backupKey = this.key + ".corrupt";

        try {
            if (storage.getItem(backupKey) === null) storage.setItem(backupKey, raw);
        } catch (e) { }

    }

}
